import { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { useTranslate } from '../../../i18n/useTranslationModules';
import { BrandliveEvent, LanguagesAbbr, NavMainItems, PageLinkType, Session } from '../../../types/working-model';
import { directoryNavItem, getNavItemLabel } from '../navbar/navbar.utils';
import { useLanguageParam } from '../utils';
import { DebugDefaultRouteMap, DefaultRouteMap } from './use-route-map';

type RouteKey = keyof typeof DefaultRouteMap;
type TranslateFunction = ReturnType<typeof useTranslate>['t'];

// most specific first - fireside and breakout live under session/:sessionUuid
const routeKeys: RouteKey[] = [
	'Breakout',
	'Fireside',
	'Session',
	'FiresideNoHomepage',
	'CustomPage',
	'AttendeeProfile',
	'Profile',
	'Watchlist',
	'Registration',
	'PrivacyPolicy',
	'TermsConditions',
	'Home',
	'Sessions',
	'People',
	'Directory',
	'Leaderboard',
	'Landing',
];

const getSessionTitle = (session: Session | undefined, t: TranslateFunction, language: LanguagesAbbr): string => {
	if (!session) return '';
	const fallback = (session.title?.[language] as string) || session.title?.base || '';
	return t(`landing:agenda.session.${session.session}.title`, fallback);
};

const getMainNavLabel = (eventBundle: BrandliveEvent, name: NavMainItems, t: TranslateFunction, language: LanguagesAbbr): string => {
	const navItem = eventBundle.homepage?.event_main_nav_items?.find(item => item.name === name)
		?? (name === NavMainItems.Directory ? directoryNavItem : undefined);

	return navItem ? getNavItemLabel(navItem, t, language) : t(`homepage:${name}`, name);
};

const getPageName = (
	eventBundle: BrandliveEvent,
	routeKey: RouteKey,
	params: Record<string, string | undefined>,
	t: TranslateFunction,
	language: LanguagesAbbr
): string => {
	switch (routeKey) {
		case 'Session':
		case 'Fireside':
		case 'Breakout':
			return getSessionTitle(eventBundle.sessions?.find(session => session.uuid === params.sessionUuid), t, language);
		case 'FiresideNoHomepage':
			return getSessionTitle(eventBundle.sessions?.[0], t, language);
		case 'CustomPage': {
			const page = eventBundle.custom_pages?.find(customPage => customPage.path === params.customPath);
			if (!page) return '';
			// mirrors the label used in custom-navigation-item
			const name = t(`home:nav.customPage.${page.custom_page}.page_name`, '')?.trim();
			return name || t(`home:nav.customPage.${page.page_name.base}`, (page.page_name?.[language] as string) ?? page.page_name.base);
		}
		case 'Profile':
		case 'AttendeeProfile':
			return t('homepage:Profile', 'Profile');
		case 'Watchlist':
			return t('homepage:Watchlist', 'Watchlist');
		case 'Registration':
			return t('homepage:registration.Registration', 'Registration');
		case 'PrivacyPolicy':
			return t('homepage:registration.footer.Privacy Policy', 'Privacy Policy');
		case 'TermsConditions':
			const TermsConditions = eventBundle.homepage?.footer?.navItems?.find(navItem => navItem?.content?.page === PageLinkType.termsAndConditions);
			const buttonName = TermsConditions?.button_name;

			return buttonName?.[language] as string ?? buttonName?.base ?? 'Terms and Conditions';
		case 'Home':
			return getMainNavLabel(eventBundle, NavMainItems.Home, t, language);
		case 'Sessions':
			return getMainNavLabel(eventBundle, NavMainItems.Sessions, t, language);
		case 'People':
		case 'Directory':
			return getMainNavLabel(eventBundle, NavMainItems.Directory, t, language);
		case 'Leaderboard':
			return getMainNavLabel(eventBundle, NavMainItems.Leaderboard, t, language);
		case 'Landing': {
			return '';
		}
		default:
			return '';
	}
};

/**
 * Sets document.title to "<event name> - <page name>" and keeps it in sync with navigation.
 * Must be used inside the live event router so location changes are picked up.
 */
export default function useDocumentTitle(debug?: boolean): void {
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const { pathname } = useLocation();
	const language = useLanguageParam();
	const { t } = useTranslate(['homepage', 'home', 'landing']);

	const title = useMemo(() => {
		if (!eventBundle?.name) return '';

		const routeMap = debug ? DebugDefaultRouteMap : DefaultRouteMap;
		let pageName = '';
		for (const routeKey of routeKeys) {
			const match = matchPath({ path: routeMap[routeKey], end: true }, pathname);
			if (match) {
				pageName = getPageName(eventBundle, routeKey, match.params, t, language).trim();
				break;
			}
		}

		return pageName ? `${eventBundle.name} - ${pageName}` : eventBundle.name;
	}, [debug, eventBundle, language, pathname, t]);

	useEffect(() => {
		if (!title) return;

		const previousTitle = document.title;
		document.title = title;
		return () => {
			document.title = previousTitle;
		};
	}, [title]);
}
